// ============================================================
// ADMOB LAYER - rewarded ads per supportare il progetto
// ============================================================
// Su nativo (Android/iOS) usa @capacitor-community/admob (da installare).
// Su web fa un fallback: mostra un "ad simulato" di 3 secondi
// poi dà la ricompensa (per dev/testing).
//
// Configurazione env (.env):
//   VITE_ADMOB_APP_ID=ca-app-pub-XXXX~XXXX
//   VITE_ADMOB_REWARDED_ID=ca-app-pub-XXXX/XXXX
//
// IMPORTANTE: per attivare AdMob su nativo, installa il plugin:
//   npm install @capacitor-community/admob
//   npx cap sync
// Senza il plugin, su nativo si usa il fallback web.
// ============================================================

import { Capacitor } from '@capacitor/core';

const ADMOB_APP_ID = (import.meta.env.VITE_ADMOB_APP_ID as string || '').trim();
const ADMOB_REWARDED_ID = (import.meta.env.VITE_ADMOB_REWARDED_ID as string || '').trim();

// Nome del modulo plugin come stringa (Vite non può analizzarlo staticamente)
const ADMOB_MODULE = '@capacitor-community/admob';

let _initialized = false;
let _pluginAvailable: boolean | null = null;

export function isAdMobConfigured(): boolean {
  return !!ADMOB_APP_ID && !!ADMOB_REWARDED_ID;
}

/** Carica il plugin AdMob dinamicamente (ritorna null se non installato). */
async function loadAdMobPlugin(): Promise<any | null> {
  if (!Capacitor.isNativePlatform()) return null;
  if (_pluginAvailable === false) return null;
  try {
    // Usa variable per evitare che Vite cerchi di risolvere il modulo al build
    const mod = await (Function('m', 'return import(m)')(ADMOB_MODULE));
    _pluginAvailable = true;
    return mod.AdMob || mod?.default?.AdMob || mod;
  } catch {
    _pluginAvailable = false;
    return null;
  }
}

async function initAdMob(): Promise<boolean> {
  if (!isAdMobConfigured()) return false;
  if (_initialized) return true;

  const AdMob = await loadAdMobPlugin();
  if (AdMob && typeof AdMob.initialize === 'function') {
    try {
      await AdMob.initialize({ initializeForTesting: false });
    } catch (e) {
      console.warn('[admob] init error', e);
    }
  }
  _initialized = true;
  return true;
}

export interface RewardedAdResult {
  success: boolean;
  reward: boolean;
  message: string;
}

/**
 * Mostra una rewarded ad. Se l'utente la guarda fino alla fine,
 * ritorna reward=true. Su web usa una simulazione.
 */
export async function showRewardedAd(): Promise<RewardedAdResult> {
  const ok = await initAdMob();
  if (!ok) {
    return { success: false, reward: false, message: 'ads_not_configured' };
  }

  // Plugin nativo (se disponibile)
  const AdMob = await loadAdMobPlugin();
  if (AdMob && typeof AdMob.prepareRewardVideoAd === 'function') {
    try {
      await AdMob.prepareRewardVideoAd({ adId: ADMOB_REWARDED_ID });
      await AdMob.showRewardVideoAd();
      return { success: true, reward: true, message: 'ads_thanks' };
    } catch (e) {
      console.warn('[admob] native show error', e);
      // Fallback alla simulazione
    }
  }

  // Web fallback: simulazione di 3 secondi
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({ success: true, reward: true, message: 'ads_thanks' });
    }, 3000);
  });
}
